import { z } from "zod";
import { optional } from "./appointments";
import { recordId } from "./contracts";
import { UNIT_PAGE_SIZE_MAX } from "./units";
import { searchTextUpTo, unset } from "./fields";
import { PROPERTY_NAME_MAX_LENGTH, PROPERTY_PAGE_SIZE_MAX, propertyClass, propertyCreateRequest, propertyDetailsRequest, propertyStructure, propertyType, type PropertyClass, type PropertyStructure, type PropertyType } from "./properties";

const count = z.number().int().nonnegative();

export const adminProperty = z.object({
  id: recordId,
  organization: recordId.nullable(),
  name: z.string(),
  address: z.string().nullable(),
  street: z.string().nullable(),
  city: z.string().nullable(),
  county: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  image: z.string().nullable(),
  description: z.string().nullable(),
  website: z.string().nullable(),
  yearBuilt: z.number().nullable(),
  floors: z.number().nullable(),
  type: z.array(propertyType),
  structure: z.array(propertyStructure),
  class: z.array(propertyClass),
  userCount: count,
  unitCount: count,
  createdAt: z.string(),
});
export type AdminProperty = z.infer<typeof adminProperty>;

export const adminPropertyPage = z.object({ items: z.array(adminProperty), total: count });
export type AdminPropertyPage = z.infer<typeof adminPropertyPage>;

const page = z.preprocess(unset, z.coerce.number().int().min(1).max(10_000).default(1));

const pageSizeUpTo = (max: number, fallback: number) => z.preprocess(unset, z.coerce.number().int().min(1).max(max).default(fallback));

export const adminPropertyListQuery = z.object({
  search: optional(searchTextUpTo(PROPERTY_NAME_MAX_LENGTH)),
  type: optional(propertyType),
  structure: optional(propertyStructure),
  class: optional(propertyClass),
  page,
  pageSize: pageSizeUpTo(PROPERTY_PAGE_SIZE_MAX, 20),
});
export type AdminPropertyListQuery = z.infer<typeof adminPropertyListQuery>;

const distinct = <T>(values: T[]): T[] => [...new Set(values)];

export const adminPropertyRequest = propertyCreateRequest
  .omit({ customer: true, yearBuilt: true, floors: true, types: true, structures: true, classes: true, billToName: true, billToAddress: true })
  .extend({
    yearBuilt: propertyDetailsRequest.shape.yearBuilt.nullable(),
    floors: propertyDetailsRequest.shape.floors.nullable(),
    type: z.array(propertyType).transform(distinct),
    structure: z.array(propertyStructure).transform(distinct),
    class: z.array(propertyClass).transform(distinct),
  })
  .strict();
export type AdminPropertyRequest = {
  name: string;
  description: string | null;
  address: string;
  street: string | null;
  city: string | null;
  county: string | null;
  state: string | null;
  zip: string | null;
  latitude?: number | null;
  longitude?: number | null;
  website: string | null;
  yearBuilt: number | null;
  floors: number | null;
  type: PropertyType[];
  structure: PropertyStructure[];
  class: PropertyClass[];
};

export const adminListQuery = z.object({
  search: optional(searchTextUpTo(PROPERTY_NAME_MAX_LENGTH)),
  page,
  pageSize: pageSizeUpTo(UNIT_PAGE_SIZE_MAX, 20),
});
export type AdminListQuery = z.infer<typeof adminListQuery>;

export const adminPropertyUnit = z.object({
  id: recordId,
  property: recordId,
  unitNumber: z.string(),
  floorPlan: z.string().nullable(),
  beds: z.number().nonnegative(),
  baths: z.number().nonnegative(),
});
export type AdminPropertyUnit = z.infer<typeof adminPropertyUnit>;

export const adminPropertyUnitPage = z.object({ items: z.array(adminPropertyUnit), total: count });
export type AdminPropertyUnitPage = z.infer<typeof adminPropertyUnitPage>;

export const adminPropertyUser = z.object({
  id: recordId,
  property: recordId,
  user: z.object({ id: recordId, name: z.string(), email: z.string().nullable(), image: z.string().nullable() }),
  isPrimaryContact: z.boolean(),
  role: z.string().nullable(),
  notes: z.string().nullable(),
});
export type AdminPropertyUser = z.infer<typeof adminPropertyUser>;

export const adminPropertyUserPage = z.object({ items: z.array(adminPropertyUser), total: count });
export type AdminPropertyUserPage = z.infer<typeof adminPropertyUserPage>;

const linkedProperty = z.object({ id: recordId, name: z.string(), address: z.string().nullable() });

const propertyClient = z.object({ id: recordId, name: z.string(), email: z.string().nullable() }).nullable();

export const adminVendorProperty = z.object({ property: linkedProperty, client: propertyClient, active: z.boolean(), createdAt: z.string() });
export type AdminVendorProperty = z.infer<typeof adminVendorProperty>;

export const adminVendorPropertyPage = z.object({ items: z.array(adminVendorProperty), total: count });
export type AdminVendorPropertyPage = z.infer<typeof adminVendorPropertyPage>;

export const adminVendorPropertyQuery = adminListQuery.extend({ status: optional(z.enum(["active", "inactive"])) });
export type AdminVendorPropertyQuery = z.infer<typeof adminVendorPropertyQuery>;

export const adminLinkablePropertyQuery = adminListQuery;
export type AdminLinkablePropertyQuery = AdminListQuery;

export const adminLinkableProperty = z.object({ property: linkedProperty, client: propertyClient });
export type AdminLinkableProperty = z.infer<typeof adminLinkableProperty>;

export const adminLinkablePropertyPage = z.object({ items: z.array(adminLinkableProperty), total: count });
export type AdminLinkablePropertyPage = z.infer<typeof adminLinkablePropertyPage>;
